import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeySquare, Copy, Trash2, Plus, BookOpen, BarChart3, Clock } from "lucide-react";

const ALL_SCOPES = [["read", "Read"], ["query", "Query"], ["render", "Render exports"]];

function relTime(iso) {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const m = Math.round(Math.max(0, Date.now() - t) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d < 7 ? `${d}d ago` : new Date(iso).toLocaleDateString();
}

export function ApiTokensCard() {
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState(["read", "query"]);
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statsFor, setStatsFor] = useState(null); // token doc
  const [stats, setStats] = useState(null);
  const load = () => api.get("/api-tokens").then(({ data }) => setTokens(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = (s) => setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  const create = async () => {
    if (!name.trim()) return toast.error("Name the token (e.g. 'CI pipeline')");
    setBusy(true);
    try {
      const { data } = await api.post("/api-tokens", { name: name.trim(), scopes });
      setFresh(data.token); setName(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const revoke = async (id) => { try { await api.delete(`/api-tokens/${id}`); toast.success("Revoked"); load(); } catch { toast.error("Failed"); } };
  const copy = async (v) => {
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(v);
      else {
        const ta = document.createElement("textarea"); ta.value = v; ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success("Token copied");
    } catch { toast.error("Copy blocked — select the token and press ⌘/Ctrl-C"); }
  };
  const openStats = async (t) => {
    setStatsFor(t); setStats(null);
    try { const { data } = await api.get(`/api-tokens/${t.id}/stats`); setStats(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load stats"); }
  };
  return (
    <Card className="glass-card border-0 p-6" data-testid="api-tokens-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <KeySquare className="w-5 h-5 text-[color:var(--brand-ocean)]" />
          <h3 className="font-display text-lg text-[color:var(--ink)]">API tokens</h3>
        </div>
        <Link to="/developer" data-testid="api-docs-link"
          className="text-xs flex items-center gap-1 text-[color:var(--brand-ocean)] hover:underline">
          <BookOpen className="w-3.5 h-3.5" />View API docs
        </Link>
      </div>
      <p className="text-sm text-[color:var(--ink-3)] mt-2">Programmatic access to the public REST API (<code className="text-xs">/api/v1</code>). Calls run with your permissions — tenant isolation and row-level security are enforced server-side.</p>
      {fresh && (
        <div data-testid="api-token-fresh" className="mt-3 rounded-xl border border-[color:var(--brand-ocean)] bg-[color:rgba(43,78,255,0.06)] p-3">
          <div className="text-[11px] font-semibold text-[color:var(--brand-ocean)] mb-1">Copy this token now — it won&apos;t be shown again</div>
          <div className="flex gap-2 items-center">
            <code className="text-[11px] break-all flex-1">{fresh}</code>
            <Button size="sm" variant="outline" onClick={() => copy(fresh)} data-testid="api-token-copy" className="shrink-0 h-7"><Copy className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      )}
      <div className="flex gap-2 mt-4">
        <Input data-testid="api-token-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Token name (e.g. CI pipeline)" className="bg-[color:var(--surface)]" />
        <Button data-testid="api-token-create" onClick={create} disabled={busy} className="bg-[color:var(--brand-ocean)] text-white shrink-0"><Plus className="w-4 h-4 mr-1" />Create</Button>
      </div>
      <div className="flex flex-wrap gap-3 mt-2">
        {ALL_SCOPES.map(([s, label]) => (
          <label key={s} className="flex items-center gap-1.5 text-xs text-[color:var(--ink-2)]">
            <Checkbox data-testid={`api-scope-${s}`} checked={scopes.includes(s)} onCheckedChange={() => toggle(s)} />{label}
          </label>
        ))}
      </div>
      {tokens.length > 0 && (
        <div className="mt-4 space-y-1.5">
          {tokens.map((t) => (
            <div key={t.id} data-testid={`api-token-row-${t.id}`} className={`flex items-center justify-between text-xs rounded-lg px-3 py-2 bg-[color:var(--surface-2)] ${t.revoked ? "opacity-50" : ""}`}>
              <span className="truncate flex-1">
                <span className="font-medium text-[color:var(--ink)]">{t.name}</span>
                <span className="text-[color:var(--ink-3)] ml-2">{t.hint}</span>
                <span className="text-[10px] text-[color:var(--brand-ocean)] ml-2">{(t.scopes || []).join(" · ")}</span>
                {t.revoked && <span className="text-[color:var(--danger)] ml-2">revoked</span>}
              </span>
              <span className="flex items-center gap-3 shrink-0">
                <span data-testid={`api-token-calls-${t.id}`} className="flex items-center gap-1 text-[color:var(--ink-3)]" title="Total API calls">
                  <BarChart3 className="w-3 h-3" />{t.call_count || 0}
                </span>
                <span data-testid={`api-token-last-${t.id}`} className="flex items-center gap-1 text-[color:var(--ink-3)]" title="Last used">
                  <Clock className="w-3 h-3" />{relTime(t.last_used_at)}
                </span>
                <button data-testid={`api-token-stats-${t.id}`} onClick={() => openStats(t)} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]" title="View analytics">
                  <BarChart3 className="w-3.5 h-3.5" />
                </button>
                {!t.revoked && <button data-testid={`api-token-revoke-${t.id}`} onClick={() => revoke(t.id)} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]" title="Revoke"><Trash2 className="w-3.5 h-3.5" /></button>}
              </span>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!statsFor} onOpenChange={(v) => { if (!v) { setStatsFor(null); setStats(null); } }}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><BarChart3 className="w-4 h-4" />Analytics · {statsFor?.name}</DialogTitle></DialogHeader>
          {!stats ? <div className="text-sm text-[color:var(--ink-3)] py-6 text-center">Loading…</div> : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="rounded-xl bg-[color:var(--surface-2)] p-3">
                  <div data-testid="api-token-stats-calls" className="font-display text-2xl text-[color:var(--ink)]">{stats.call_count || 0}</div>
                  <div className="text-[11px] text-[color:var(--ink-3)] uppercase tracking-wider">Total calls</div>
                </div>
                <div className="rounded-xl bg-[color:var(--surface-2)] p-3">
                  <div data-testid="api-token-stats-last" className="font-display text-2xl text-[color:var(--ink)]">{relTime(stats.last_used_at)}</div>
                  <div className="text-[11px] text-[color:var(--ink-3)] uppercase tracking-wider">Last used</div>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-[color:var(--ink-2)] mb-1.5">Top endpoints</div>
                {(!stats.top_endpoints || stats.top_endpoints.length === 0) ? (
                  <div data-testid="api-token-stats-empty" className="text-xs text-[color:var(--ink-3)] italic bg-[color:var(--surface-2)] rounded-lg px-3 py-3 text-center">No calls yet — usage will appear here after the first successful API request.</div>
                ) : (
                  <div className="space-y-1">
                    {stats.top_endpoints.map((e, idx) => {
                      const total = stats.top_endpoints.reduce((a, b) => a + b.count, 0) || 1;
                      const pct = Math.round((e.count / total) * 100);
                      return (
                        <div key={idx} data-testid={`api-token-stats-row-${idx}`} className="text-xs">
                          <div className="flex justify-between">
                            <code className="text-[color:var(--ink-2)] truncate mr-2">{e.endpoint}</code>
                            <span className="text-[color:var(--ink-3)] shrink-0">{e.count} · {pct}%</span>
                          </div>
                          <div className="h-1 mt-0.5 rounded bg-[color:var(--surface-2)] overflow-hidden">
                            <div className="h-full bg-[color:var(--brand-ocean)]" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {stats.last_endpoint && (
                <div className="text-[11px] text-[color:var(--ink-3)]">Last endpoint: <code className="text-[color:var(--ink-2)]">{stats.last_endpoint}</code></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
